import React from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import { type TabData } from "@utils/dashboard-storage";
import HPButton from "@/components/common/Button";

interface AddGroupPanelProps {
  localTabs: TabData[];
  name: string;
  nameError: string | null;
  selectedTabIds: Set<string>;
  canConfirm: boolean;
  previewTabs: TabData[];
  onNameChange: (value: string) => void;
  onToggleTab: (tabId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const AddGroupPanel: React.FC<AddGroupPanelProps> = ({
  localTabs,
  name,
  nameError,
  selectedTabIds,
  canConfirm,
  previewTabs,
  onNameChange,
  onToggleTab,
  onConfirm,
  onCancel,
}) => {
  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="subtitle2" className="font-semibold mb-2">
        New group name
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        inputProps={{ maxLength: 40 }}
        error={!!nameError}
        helperText={nameError}
        placeholder="e.g. Priority Market Views"
        sx={{ mb: 2 }}
      />

      <Typography variant="subtitle2" className="font-semibold mb-1">
        Select at least 2 tabs
      </Typography>
      <List dense sx={{ maxHeight: 260, overflowY: "auto", border: "1px solid #E0E0E0", borderRadius: "8px" }}>
        {localTabs.map((tab) => (
          <ListItem key={tab.id} disablePadding>
            <ListItemButton onClick={() => onToggleTab(tab.id)} dense>
              <Checkbox
                edge="start"
                checked={selectedTabIds.has(tab.id)}
                tabIndex={-1}
                disableRipple
                size="small"
              />
              <ListItemText primary={tab.label} />
              {tab.groupName && (
                <Chip label={tab.groupName} size="small" variant="outlined" sx={{ ml: 1 }} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {previewTabs.length >= 2 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" className="font-semibold mb-1">
            Resulting order
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {previewTabs.map((tab, index) => (
            <Typography key={tab.id} variant="body2" color="text.secondary">
              {index + 1}. {tab.label}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
        <HPButton variant="outlined" size="small" onClick={onCancel}>
          Cancel
        </HPButton>
        <HPButton variant="contained" size="small" onClick={onConfirm} disabled={!canConfirm}>
          Create Group
        </HPButton>
      </Box>
    </Box>
  );
};

export default AddGroupPanel;
